(() => {
  "use strict";

  const MODAL_SELECTOR = "[data-barros-bike-modal]";
  const TRIGGER_SELECTOR = [
    "[data-barros-bike-form-open]",
    'a[href="#barros-bike-request"]',
    'button[data-barros-bike-request]'
  ].join(",");
  const CLOSE_SELECTOR = "[data-barros-bike-modal-close]";
  const DIALOG_SELECTOR = ".barros-bike-modal__dialog";
  const FOCUSABLE_SELECTOR = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    '[tabindex]:not([tabindex="-1"])'
  ].join(",");

  let activeModal = null;
  let previousFocus = null;

  function getModal(trigger = null) {
    const controls = trigger && trigger.getAttribute ? trigger.getAttribute("aria-controls") : null;

    if (controls) {
      const controlledModal = document.getElementById(controls);
      if (controlledModal && controlledModal.matches(MODAL_SELECTOR)) return controlledModal;
    }

    return document.querySelector(MODAL_SELECTOR);
  }

  /*
    Product-arrival modals are initially rendered inside the PDP info column.
    That column can become a containing block because of sticky/animation CSS,
    which makes a fixed modal appear shifted or clipped. Moving only this modal
    to <body> at open time guarantees true viewport centering without changing
    the shared Concierge modal used elsewhere on the site.
  */
  function ensureViewportRoot(modal) {
    if (!modal || !modal.hasAttribute("data-barros-product-arrival-modal")) return;

    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }
  }

  function lockPage() {
    document.documentElement.classList.add("barros-bike-modal-lock");
    document.body.classList.add("barros-bike-modal-lock");
  }

  function unlockPage() {
    document.documentElement.classList.remove("barros-bike-modal-lock");
    document.body.classList.remove("barros-bike-modal-lock");
  }

  function openModal(modal, trigger = null) {
    if (!modal) return;

    ensureViewportRoot(modal);

    if (activeModal && activeModal !== modal) closeModal(activeModal, false);

    previousFocus = trigger || document.activeElement;
    activeModal = modal;
    modal.classList.add("is-open");
    modal.setAttribute("aria-hidden", "false");
    lockPage();

    window.requestAnimationFrame(() => {
      const dialog = modal.querySelector(DIALOG_SELECTOR);
      if (dialog) dialog.focus({ preventScroll: true });
    });
  }

  function closeModal(modal, restoreFocus = true) {
    if (!modal) return;

    modal.classList.remove("is-open");
    modal.setAttribute("aria-hidden", "true");
    unlockPage();

    if (activeModal === modal) activeModal = null;

    if (restoreFocus && previousFocus && typeof previousFocus.focus === "function") {
      window.requestAnimationFrame(() => previousFocus.focus({ preventScroll: true }));
    }

    previousFocus = null;
  }

  function trapFocus(event) {
    if (!activeModal || event.key !== "Tab") return;

    const focusable = Array.from(activeModal.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (element) => element.offsetParent !== null
    );

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function handleKeydown(event) {
    if (!activeModal) return;

    if (event.key === "Escape") {
      event.preventDefault();
      closeModal(activeModal);
      return;
    }

    trapFocus(event);
  }

  function handleClick(event) {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest(TRIGGER_SELECTOR);

    if (trigger) {
      const modal = getModal(trigger);
      if (modal) {
        event.preventDefault();
        openModal(modal, trigger);
      }
      return;
    }

    const closeButton = event.target.closest(CLOSE_SELECTOR);
    if (closeButton && activeModal) {
      event.preventDefault();
      closeModal(activeModal);
      return;
    }

    if (activeModal && activeModal.contains(event.target)) {
      const dialog = activeModal.querySelector(DIALOG_SELECTOR);
      if (!dialog || !dialog.contains(event.target)) {
        event.preventDefault();
        closeModal(activeModal);
      }
    }
  }

  function openSuccessModalIfNeeded() {
    const success = document.querySelector(`${MODAL_SELECTOR} .barros-bike-modal__success`);
    if (!success) return;

    const modal = success.closest(MODAL_SELECTOR);
    if (modal) openModal(modal);
  }

  document.addEventListener("click", handleClick);
  document.addEventListener("keydown", handleKeydown);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", openSuccessModalIfNeeded, { once: true });
  } else {
    openSuccessModalIfNeeded();
  }

  document.addEventListener("shopify:section:load", () => {
    if (activeModal && !document.contains(activeModal)) {
      activeModal = null;
      unlockPage();
    }
  });

  document.addEventListener("shopify:section:unload", (event) => {
    if (activeModal && event.target.contains(activeModal)) closeModal(activeModal, false);
  });
})();
